import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../services/api';
import { Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { extractAxiosError } from '../components/SafeError';
import AuthHeader from '../components/AuthHeader';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authApi.forgotPassword({ email });
      setSent(true);
    } catch (err) {
      setError(extractAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
        <div className="absolute inset-0 bg-black/80" />
        
        <div className="relative w-full max-w-md" data-testid="forgot-password-sent">
          <div className="card-panel p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-status-success/20 rounded-full mb-4">
              <CheckCircle className="w-8 h-8 text-status-success" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">Check Your Email</h1>
            <p className="text-cockpit-text-secondary mb-6">
              If an account exists for <strong className="text-cockpit-text-primary">{email}</strong>, 
              you'll receive a password reset link shortly.
            </p>
            <Link to="/login" className="btn-secondary inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
      <div className="absolute inset-0 bg-black/80" />
      
      <div className="relative w-full max-w-md" data-testid="forgot-password-page">
        <div className="card-panel p-8">
          {/* Header */}
          <AuthHeader title="Reset Password" subtitle="Enter your email to receive a reset link" />

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-900 rounded-sm">
              <p className="text-sm text-red-300" data-testid="forgot-error">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label-text block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="pilot@example.com"
                required
                data-testid="forgot-email"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="forgot-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-cockpit-text-secondary hover:text-airbus-blue inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
